/**
 * Preferências leves da home — só no browser (sem API).
 * Por quê: Carla esconde discovery ou escolhe o gráfico sem wait de backend.
 */

export type SeriesMetricMode = "volume" | "engagement" | "goals";

const PREFS_KEY = "dashboard_home_prefs_v1";
const LAST_CAMPAIGN_KEY = "dashboard_last_campaign_edit_id";
const TOUR_DONE_KEY = "dashboard_tour_v1_done";

export type DashboardHomePrefs = {
  hideDiscovery: boolean;
  seriesMetric: SeriesMetricMode;
};

const DEFAULTS: DashboardHomePrefs = {
  hideDiscovery: false,
  seriesMetric: "volume",
};

export function readDashboardPrefs(): DashboardHomePrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) {
      return { ...DEFAULTS };
    }
    const parsed = JSON.parse(raw) as Partial<DashboardHomePrefs>;
    const metric = parsed.seriesMetric;
    return {
      hideDiscovery: Boolean(parsed.hideDiscovery),
      seriesMetric:
        metric === "engagement" || metric === "goals" || metric === "volume"
          ? metric
          : "volume",
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function writeDashboardPrefs(
  patch: Partial<DashboardHomePrefs>
): DashboardHomePrefs {
  const next = { ...readDashboardPrefs(), ...patch };
  localStorage.setItem(PREFS_KEY, JSON.stringify(next));
  return next;
}

export function rememberLastCampaignEdit(campaignId: number): void {
  if (!campaignId || campaignId < 1) {
    return;
  }
  localStorage.setItem(LAST_CAMPAIGN_KEY, String(campaignId));
}

export function readLastCampaignEditId(): number | null {
  try {
    const raw = localStorage.getItem(LAST_CAMPAIGN_KEY);
    const id = raw ? Number(raw) : NaN;
    return Number.isFinite(id) && id > 0 ? id : null;
  } catch {
    return null;
  }
}

export function isDashboardTourDone(): boolean {
  return localStorage.getItem(TOUR_DONE_KEY) === "1";
}

export function markDashboardTourDone(): void {
  localStorage.setItem(TOUR_DONE_KEY, "1");
}

/** Compara 2ª metade vs 1ª metade da série (ex. 14d → semana atual vs anterior). */
export function compareSeriesHalves(
  points: Array<{ sent: number; opened: number; bounced: number }>
): {
  openRatePp: number | null;
  sentDeltaPct: number | null;
  /** Pontos percentuais: bounce atual − anterior (positivo = piorou). */
  bounceRatePp: number | null;
  hasCompare: boolean;
} {
  if (points.length < 4) {
    return {
      openRatePp: null,
      sentDeltaPct: null,
      bounceRatePp: null,
      hasCompare: false,
    };
  }
  const mid = Math.floor(points.length / 2);
  const prev = points.slice(0, mid);
  const curr = points.slice(mid);
  const sum = (arr: typeof points, key: "sent" | "opened" | "bounced") =>
    arr.reduce((acc, p) => acc + (p[key] || 0), 0);
  const prevSent = sum(prev, "sent");
  const currSent = sum(curr, "sent");
  const prevOpened = sum(prev, "opened");
  const currOpened = sum(curr, "opened");
  const prevBounced = sum(prev, "bounced");
  const currBounced = sum(curr, "bounced");
  if (prevSent <= 0 && currSent <= 0) {
    return {
      openRatePp: null,
      sentDeltaPct: null,
      bounceRatePp: null,
      hasCompare: false,
    };
  }
  const prevRate = prevSent > 0 ? (prevOpened / prevSent) * 100 : 0;
  const currRate = currSent > 0 ? (currOpened / currSent) * 100 : 0;
  const openRatePp = Math.round((currRate - prevRate) * 10) / 10;
  const prevBounce = prevSent > 0 ? (prevBounced / prevSent) * 100 : 0;
  const currBounce = currSent > 0 ? (currBounced / currSent) * 100 : 0;
  const bounceRatePp = Math.round((currBounce - prevBounce) * 10) / 10;
  const sentDeltaPct =
    prevSent > 0
      ? Math.round(((currSent - prevSent) / prevSent) * 1000) / 10
      : null;
  return { openRatePp, sentDeltaPct, bounceRatePp, hasCompare: true };
}

const BOUNCE_TOAST_KEY = "dashboard_bounce_worsen_toast_day";

/** Toast no máx. 1× por dia UTC se bounce piorou vs semana anterior. */
export function shouldShowBounceWorsenToast(bounceRatePp: number | null): boolean {
  if (bounceRatePp == null || bounceRatePp <= 0) {
    return false;
  }
  try {
    const day = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(BOUNCE_TOAST_KEY) === day) {
      return false;
    }
    localStorage.setItem(BOUNCE_TOAST_KEY, day);
    return true;
  } catch {
    return false;
  }
}

/** CSV simples da série (UTF-8 BOM p/ Excel). */
export function seriesToCsv(
  points: Array<Record<string, string | number>>,
  columns: string[]
): string {
  const esc = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [
    columns.join(","),
    ...points.map((p) => columns.map((c) => esc(p[c] ?? "")).join(",")),
  ];
  return `\uFEFF${lines.join("\n")}`;
}

export function downloadTextFile(
  filename: string,
  content: string,
  mime = "text/csv;charset=utf-8"
): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
